import apiClient from './api';
import {
  GetAllOrdersResponse,
  GetOrderDetailsResponse,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  GetVendorsWithOrdersResponse,
  GetUsersWithOrdersResponse,
  OrderStatus,
  AdminOrderListItem,
} from '../types/orders.types';

export interface PaginatedOrdersResponse {
  items: AdminOrderListItem[];
  afterCursor?: string;
}

export const ordersService = {
  /**
   * Get orders by status with cursor pagination (NEW - optimized)
   * Uses the new backend endpoint with GSI for efficient queries
   */
  getOrdersByStatus: async (
    status: string,
    afterCursor?: string,
    limit?: number
  ): Promise<PaginatedOrdersResponse> => {
    const params = new URLSearchParams();
    if (afterCursor) {
      params.append('afterCursor', afterCursor);
    }
    if (limit) {
      params.append('limit', limit.toString());
    }

    const queryString = params.toString();
    // Handle 'all' status specially - it uses a different endpoint
    const statusPath = status === 'all' ? 'all' : status.toLowerCase();
    const url = `/admins/orders/${statusPath}${queryString ? `?${queryString}` : ''}`;

    // Backend wraps response in { success, data, timestamp }
    // We need to unwrap it to get { items, afterCursor }
    const response = await apiClient.get<{ success: boolean; data: PaginatedOrdersResponse }>(url);
    return response.data.data;
  },

  /**
   * Get all orders with optional filtering and pagination (LEGACY)
   * This method is kept for backward compatibility
   */
  getAllOrders: async (params: {
    limit?: number;
    afterCursor?: string;
    status?: OrderStatus;
    vendorId?: string;
    userId?: string;
    search?: string;
    section?: 'all' | 'disputed' | 'refunds';
  }): Promise<GetAllOrdersResponse> => {
    const response = await apiClient.get<GetAllOrdersResponse>('/admins/orders', {
      params: {
        limit: params.limit || 20,
        afterCursor: params.afterCursor,
        status: params.status,
        vendorId: params.vendorId,
        userId: params.userId,
        search: params.search,
        section: params.section || 'all',
      },
    });
    return response.data;
  },

  /**
   * Get order details by ID
   */
  getOrderDetails: async (orderId: string): Promise<GetOrderDetailsResponse> => {
    const response = await apiClient.get<GetOrderDetailsResponse>(`/admins/orders/${orderId}`);
    return response.data;
  },

  /**
   * Update order status
   */
  updateOrderStatus: async (
    orderId: string,
    data: UpdateOrderStatusRequest
  ): Promise<UpdateOrderStatusResponse> => {
    const response = await apiClient.put<UpdateOrderStatusResponse>(
      `/admins/orders/${orderId}/status`,
      data
    );
    return response.data;
  },

  /**
   * Get list of vendors that have orders
   */
  getVendorsWithOrders: async (): Promise<GetVendorsWithOrdersResponse> => {
    const response = await apiClient.get<GetVendorsWithOrdersResponse>('/admins/orders/vendors');
    return response.data;
  },

  /**
   * Get list of users/clients that have orders
   */
  getUsersWithOrders: async (): Promise<GetUsersWithOrdersResponse> => {
    const response = await apiClient.get<GetUsersWithOrdersResponse>('/admins/orders/users');
    return response.data;
  },
};
